import styled from "@emotion/styled";
import { Global, css } from "@emotion/react";
import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import type { ForwardRefExoticComponent, RefAttributes } from "react";
import { motion } from "framer-motion";
import { ReactSortable } from "react-sortablejs";

import { paper, fonts, easing, shade, gloss, bp } from "../theme";
import { useWebSocket } from "../useWebSocket";
import { rejoinStorage } from "../rejoinStorage";
import type { CommonRoomData, WordPickerView as WordPickerViewType } from "../protocol";

import {
    MsScreen, MsFooter,
    MsEyebrow,
    MsCompose, MsComposePlaceholder, MsComposeWord,
    MsProseWord, MsBtn, MsSourceNote, PaperChips,
    ServerTimerRule,
    Stack,
} from "../ms";
import HoldToConfirmButton from "./HoldToConfirmButton";

/* ───────────────────────────────────────────────────────────────────
 * Word picker is the classic phone-app-shell: prompt + compose strip
 * pinned at the top, word pool scrolling in the middle, submit/clear
 * pinned at the bottom. The same applies on desktop so submission stays
 * one tap away no matter how long the pool is.
 * ────────────────────────────────────────────────────────────────── */

/* MsScreen is already exactly the space the shell gives it, banner included, so
 * this no longer has to claim the viewport for itself. */
const PickerScreen = styled(MsScreen)``;

const HeaderArea = styled.div`
    padding: 14px 22px 8px;
    width: 100%;
    max-width: var(--content-max-wide);
    margin: 0 auto;
    flex-shrink: 0;
    display: flex;
    flex-direction: column;
    gap: 12px;

    @media ${bp.wide} {
        padding: 20px 32px 8px;
        gap: 16px;
    }
`;

const PoolContainer = styled.div`
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    overflow-x: hidden;
    padding: 4px 22px 16px;
    width: 100%;
    max-width: var(--content-max-wide);
    margin: 0 auto;

    @media ${bp.wide} {
        padding: 4px 32px 16px;
    }

    &::-webkit-scrollbar { width: 4px; }
    &::-webkit-scrollbar-thumb { background: ${paper.ruleSoft}; border-radius: 2px; }
`;

/**
 * The pool, set as a column of prose.
 *
 * Justified, with a measure that stops well short of the content width — a line
 * of type running the full width of a desktop window is unreadable however
 * pretty the font is. The leading is deliberately looser than a printed page:
 * every word here is a target, and the space between lines is what keeps two
 * rows of them from becoming one.
 *
 * One fade for the whole column rather than a staggered one per word. The
 * staggered entrance was written for a tray of a dozen chips; across a few
 * thousand it's a few thousand animations.
 */
const PoolColumn = styled.div`
    max-width: 62ch;
    padding: 16px 18px 20px;
    background: ${paper.paperCool};
    border-radius: 4px;
    box-shadow:
        0 1px 0 ${gloss(0.6)} inset,
        0 0 0 1px ${shade(0.07)},
        0 10px 20px -16px ${shade(0.4)};
    text-align: justify;
    text-justify: inter-word;
    hanging-punctuation: first;
    animation: ms-fade-up 0.4s ${easing.standard} both;

    @media ${bp.wide} {
        max-width: 78ch;
        padding: 22px 26px 26px;
    }
`;

/* `pan-y`, not `none`: the strip scrolls now that it has a ceiling, and
 * `touch-action: none` would stop a finger landing on a word from moving it.
 * Horizontal panning stays disabled, so a sideways drag is still a drag. */
const DraggableComposeWord = styled(MsComposeWord)`
    cursor: grab;
    touch-action: pan-y;
    &:active { cursor: grabbing; }
`;

/* Source + attribution on the left, order control on the right. The title takes
 * whatever room is left and truncates: the control's width is fixed, the
 * article's name isn't. */
const PoolBar = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 14px;
`;

const SourceLine = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
`;

/* The article's name, credited rather than labelled — see `MsSourceNote`. It
 * still truncates: the order control's width is fixed, an article's name isn't. */
const SourceTitle = styled(MsSourceNote)`
    font-size: 15px;
    color: ${paper.inkSoft};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const SourceCredit = styled(MsSourceNote)`
    font-size: 11px;
`;

const PromptText = styled.div`
    font-family: ${fonts.serif};
    font-weight: 600;
    font-size: 18px;
    line-height: 1.32;
    color: ${paper.inkSoft};
`;

/* The header doesn't shrink and the screen is a hard 100dvh, so an unbounded
 * compose strip grows straight out of the pool's height — build a long enough
 * answer and there was nothing left to pick from. It scrolls itself instead. */
const ComposeStrip = styled(motion.div)`
    position: relative;
    max-height: 34vh;
    overflow-y: auto;
    overscroll-behavior: contain;

    &::-webkit-scrollbar { width: 4px; }
    &::-webkit-scrollbar-thumb { background: ${paper.ruleSoft}; border-radius: 2px; }
`;

const ComposePlaceholder = styled(MsComposePlaceholder)`
    pointer-events: none;
`;

/* Sortable visual overrides — float the dragged tile and gray out the
 * placeholder ghost so it reads as the tile is in motion. */
const sortableGlobals = css`
    .sortable-ghost {
        opacity: 0.35;
    }
    .sortable-drag {
        opacity: 0.95 !important;
        transform: rotate(2deg) scale(1.04) !important;
        box-shadow:
            0 0 0 1px ${shade(0.18)},
            0 14px 28px -8px ${shade(0.4)} !important;
        cursor: grabbing !important;
        z-index: 1100 !important;
    }
`;

interface PoolItem { id: string; word: string }

interface PickedItem {
    id: string;
    word: string;
    /** Which pool entry this came from. With duplicates in the passage the same
     *  word appears many times over, and the mark in Article view is on the one
     *  you actually took — the way you'd underline a word on the page rather
     *  than every other copy of it. Null for words taken from the A–Z index,
     *  which has no position in the passage to point back at. */
    poolId: string | null;
}

/**
 * How the pool is laid out. The two modes are different documents, not one list
 * sorted two ways.
 *
 * `article` is the passage as written, repeats and all, so it reads as the
 * source does and the common words are wherever you happen to be looking. `az`
 * is its index: every distinct word once, alphabetised, for when you already
 * know what you want and only need to find it.
 */
type PoolOrder = "article" | "az";
const POOL_ORDERS: PoolOrder[] = ["article", "az"];
const POOL_ORDER_LABELS = ["Article", "A–Z"];

/** Mirrors MAX_ANSWER_WORDS in WordPickerGamePhase. The server drops an answer
 *  past it in silence, so stop adding rather than let that happen unseen. */
const MAX_ANSWER_WORDS = 150;

interface Props {
    view: WordPickerViewType;
    common: CommonRoomData;
}

export default function WordPickerView({ view, common }: Props) {
    const { sendMessage } = useWebSocket();
    const { words, prompt, article } = view;
    const { round, roomCode, timeoutTime, phaseStartTime } = common;

    const nextIdRef = useRef(0);
    const pickedIdFor = () => `picked-${nextIdRef.current++}`;

    const [order, setOrder] = useState<PoolOrder>("article");

    /* Ids are stable per entry: by position in the passage for the article, by
     * the word itself for the index. Switching modes therefore swaps one document
     * for another rather than shuffling one list. */
    const poolItems = useMemo<PoolItem[]>(() => {
        if (order === "az") {
            return [...new Set(words)]
                .sort((a, b) => a.localeCompare(b))
                .map(w => ({ id: `az-${w}`, word: w }));
        }
        return words.map((w, i) => ({ id: `pool-${i}-${w}`, word: w }));
    }, [words, order]);

    /* Picked items rehydrate from any saved draft on mount. */
    const [picked, setPicked] = useState<PickedItem[]>(() => {
        const saved = rejoinStorage.getDraft(roomCode, round) ?? [];
        // A restored draft is a list of words; where in the passage each came
        // from isn't worth persisting, so they carry no mark until re-picked.
        return saved.map(w => ({ id: pickedIdFor(), word: w, poolId: null }));
    });

    useEffect(() => {
        rejoinStorage.clearOldDrafts(roomCode, round);
    }, [roomCode, round]);

    /* Push the answer to the server and persist a local draft whenever the
     * compose strip changes. */
    useEffect(() => {
        const answer = picked.map(p => p.word);
        sendMessage({ type: "update_answer", answer });
        rejoinStorage.saveDraft(roomCode, round, answer);
    }, [picked, sendMessage, roomCode, round]);

    /**
     * What to mark.
     *
     * The two documents want different answers. The index holds each word once,
     * so the word is the thing to mark. The passage holds the same word over and
     * over, and marking by word would light up forty copies of "the" to say
     * something you already know — the useful mark is on the copies you actually
     * took, the way you'd underline a word on the page rather than every other
     * copy of it.
     */
    const marked = useMemo(() => {
        const keys = new Set<string>();
        for (const p of picked) {
            const key = order === "az" ? p.word : p.poolId;
            if (key != null) keys.add(key);
        }
        return keys;
    }, [picked, order]);

    const addWord = (item: PoolItem) => {
        setPicked(p => (p.length >= MAX_ANSWER_WORDS
            ? p
            : [...p, { id: pickedIdFor(), word: item.word, poolId: order === "az" ? null : item.id }]));
    };
    const removeAt = (index: number) => {
        setPicked(p => p.filter((_, i) => i !== index));
    };
    const clearAll = () => {
        setPicked([]);
        rejoinStorage.clearDraft(roomCode, round);
    };

    const handleSubmit = () => {
        sendMessage({ type: "submit_answer", answer: picked.map(p => p.word) });
        rejoinStorage.clearDraft(roomCode, round);
    };

    if (!words) {
        return (
            <PickerScreen>
                <HeaderArea><MsEyebrow>No words available</MsEyebrow></HeaderArea>
            </PickerScreen>
        );
    }

    const blank = picked.length === 0;

    return (
        <PickerScreen>
            <Global styles={sortableGlobals} />

            <ServerTimerRule
                timeoutTime={timeoutTime}
                phaseStartTime={phaseStartTime}
                warnAt={10}
            />

            <HeaderArea>
                <motion.div layoutId="prompt-text">
                    <PromptText>
                        "{prompt.replace("___", "___________")}"
                    </PromptText>
                </motion.div>

                <ComposeStrip layoutId="compose-strip" role="group" aria-label="Your answer">
                    <ReactSortable
                        list={picked}
                        setList={setPicked}
                        animation={180}
                        ghostClass="sortable-ghost"
                        dragClass="sortable-drag"
                        delay={50}
                        delayOnTouchOnly
                        tag={MsCompose as unknown as ForwardRefExoticComponent<RefAttributes<HTMLElement>>}
                    >
                        {picked.map((p, k) => (
                            <DraggableComposeWord
                                key={p.id}
                                aria-label={`Remove ${p.word}`}
                                onClick={() => removeAt(k)}
                            >
                                {p.word}
                            </DraggableComposeWord>
                        ))}
                    </ReactSortable>
                    {blank && (
                        <ComposePlaceholder>
                            Tap words below to build your answer…
                        </ComposePlaceholder>
                    )}
                </ComposeStrip>

                <PoolBar>
                    {/* Spectators are sent no article, so there is nothing to credit. */}
                    <SourceLine>
                        {article && <>
                            <SourceTitle>from {article.title}</SourceTitle>
                            <SourceCredit>{article.attribution}</SourceCredit>
                        </>}
                    </SourceLine>
                    <PaperChips
                        size="sm"
                        options={POOL_ORDERS}
                        labels={POOL_ORDER_LABELS}
                        value={order}
                        onChange={setOrder}
                    />
                </PoolBar>
            </HeaderArea>

            <PoolContainer role="group" aria-label="Words from the article">
                <PoolColumn>
                    {poolItems.map(p => (
                        /* The trailing space is a real one, and load-bearing:
                         * justification works by stretching the spaces between
                         * words, and JSX drops the whitespace between mapped
                         * elements. Without it the column sets solid. */
                        <Fragment key={p.id}>
                            <MsProseWord
                                used={marked.has(order === "az" ? p.word : p.id)}
                                onClick={() => addWord(p)}
                            >
                                {p.word}
                            </MsProseWord>{" "}
                        </Fragment>
                    ))}
                </PoolColumn>
            </PoolContainer>

            <MsFooter wide>
                <Stack gap={8}>
                    <MsBtn variant="red" disabled={blank} onClick={handleSubmit}>Submit</MsBtn>
                    {!blank && (
                        <HoldToConfirmButton
                            text="Hold to clear"
                            activeText="Keep holding…"
                            onConfirm={clearAll}
                            variant="ghost"
                            compact
                        />
                    )}
                </Stack>
            </MsFooter>
        </PickerScreen>
    );
}
